/**
 * Several flags on one run (TRA-1088).
 *
 * A flag is a labels row, and a row is keyed by ONE trace (one gateway
 * request), so a run with findings on two requests already carries two
 * queue rows, each with its own verdict. What the page lacked was the
 * link between them: opening one flag never said the run had another.
 * These helpers group the queue by `conversation_id` so the page can show
 * every flag on the open run, pin each one on its own request, and offer
 * the requests that carry no flag yet as targets for a new one.
 *
 * Pure functions over the already-fetched queue: no new endpoint, and the
 * grouping is the same one the eval-PR builder uses (conversation first).
 */

import type { ReviewQueueItem } from '@/api/generated/model'

/** One flag on the open run, placed on its request when the transcript has it. */
export interface RunFlag {
  item: ReviewQueueItem
  /** 1-based request number, or null when the flagged trace is not in the loaded transcript. */
  request: number | null
}

/** A request of the open run that no flag points at, ready to be flagged. */
export interface FlaggableRequest {
  /** 1-based request number, the same one the ask panel cites. */
  n: number
  traceId: string
  traceKey: string
}

/**
 * Every queue row on the run `conversationId` names, dismissed ones
 * included (they still show as settled). A run without a conversation id
 * cannot be grouped, so `self` is its only flag. `self` is always in the
 * result even when the queue does not hold it (a stand-in row, TRA-1079).
 */
export function flagsOnRun(
  items: readonly ReviewQueueItem[],
  self: ReviewQueueItem,
  requestOf: ReadonlyMap<string, number>,
): RunFlag[] {
  const rows: ReviewQueueItem[] =
    self.conversation_id === null
      ? [self]
      : items.filter((it) => it.conversation_id === self.conversation_id)
  if (!rows.some((it) => it.trace_id === self.trace_id)) rows.push(self)
  const flags = rows.map((item) => ({ item, request: requestOf.get(item.trace_id) ?? null }))
  // Request order, so the strip reads like the transcript; flags whose
  // trace is not on screen go last.
  return flags.sort((a, b) => {
    if (a.request === null) return b.request === null ? 0 : 1
    if (b.request === null) return -1
    return a.request - b.request
  })
}

/**
 * Open (not dismissed) flags per conversation, for the sidebar's count.
 * Rows without a conversation id are not counted: they cannot share a run.
 */
export function openFlagCounts(items: readonly ReviewQueueItem[]): Map<string, number> {
  const counts = new Map<string, number>()
  for (const item of items) {
    if (item.conversation_id === null || item.review_status === 'dismissed') continue
    counts.set(item.conversation_id, (counts.get(item.conversation_id) ?? 0) + 1)
  }
  return counts
}

/**
 * The run's requests that carry no flag, in request order. A request whose
 * trace has no key cannot be opened for review and is left out.
 */
export function flaggableRequests(
  turns: readonly { traceId: string; traceKey: string | null }[],
  flagged: ReadonlySet<string>,
): FlaggableRequest[] {
  const out: FlaggableRequest[] = []
  turns.forEach((turn, i) => {
    if (turn.traceKey === null || flagged.has(turn.traceId)) return
    out.push({ n: i + 1, traceId: turn.traceId, traceKey: turn.traceKey })
  })
  return out
}
